import React, { useCallback, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid, Typography, Divider, Tooltip, TextField } from '@material-ui/core';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import BusinessIcon from '@material-ui/icons/Business';
import styled from 'styled-components';
import { Column } from 'react-table';
import { DraggableRowsTable } from '@components/draggable-rows-table';
import { EntitySearchMultiParty } from '@components/entity-search';
import { EntityType } from '@lib/deals';
import { orange } from '@material-ui/core/colors';
import { ScrollSectionTitle } from '@components/scroll-section-title';
import { SubSectionTitle } from 'pages/company/form/common';

const PAGE_SIZE = 10;

export interface PersonDropdownRef {
  getPersons: () => any[];
}

export interface PersonDropdownProps {
  defaultValues: any;
  name: string;
  title: string;
  subtitle: string;
  createDeal?: boolean;
}

export const CONTENT_PAGE = "content-page";

export const PersonDropdownList = forwardRef<PersonDropdownRef, PersonDropdownProps>(({ defaultValues, name = "", title = "", subtitle = "", createDeal = false }, ref) => {
  const form = useFormContext();

  const [personList, setPersonList] = useState(() =>
    (defaultValues?.[name] || []).map((person, idx) => ({ ...person, id: idx }))
  );
  const [counter, setCounter] = useState(personList.length);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [userSelected, setUserSelected] = useState("");

  const addPerson = useCallback(() => {
    setPersonList(prev => [{
      id: counter,
      name: '',
      type: EntityType.person,
      vtr_id: '',
      meta: null,
      relation_type: 'person',
      order_id: counter + 1,
      deleteable: true
    }, ...prev]);
    setCounter(prev => prev + 1);
    setCurrentPage(0);
  }, [counter]);

  const removePerson = useCallback((id) => {
    setPersonList(prev => prev.filter(p => p.id !== id));
  }, []);

  const filteredList = useMemo(() => {
    return personList.filter(person =>
      person.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [personList, searchTerm]);

  const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);

  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [totalPages, currentPage]);

  const paginatedList = useMemo(() => {
    return filteredList.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  }, [filteredList, currentPage]);

  const columns: Column<any>[] = useMemo(() => [
    {
      Header: 'Cell',
      Cell: ({ row }) => {
        const index = row.original.id;
        row.original.vtr_id = row.original.vtr_id;
        row.original.type = EntityType.person;
        return (
          <Controller
            name={`${name}[${index}]`}
            control={form.control}
            defaultValue={row.original}
            render={({ onChange }) => (
              <PaddingBottomEntitySearch>
                <EntitySearchMultiParty
                  label=""
                  fixedType={EntityType.person}
                  defaultValue={row.original}
                  hideTypeSelect
                  clearable
                  onChange={(newValue) => {
                    onChange(newValue);
                    setPersonList(prev =>
                      prev.map(p => {
                        if (p.id === index) {
                          return {
                            ...p,
                            ...newValue,
                            vtr_id: newValue.vtr_id ? newValue.vtr_id : newValue?.meta?.vtr_id,
                            person_vtr_id: newValue.person_vtr_id ? newValue.person_vtr_id : newValue?.meta?.id,
                          };
                        }
                        return p;
                      })
                    );
                  }}
                  removeDealEntities={() => removePerson(index)}
                  isDealEntityRemovable={true}
                  isDealEntityRequestCreatable={!createDeal}
                  page={CONTENT_PAGE}
                  indexOfEntities={index}
                  setUserSelected={setUserSelected}
                />
              </PaddingBottomEntitySearch>
            )}
          />
        );
      },
    },
  ], [form.control, removePerson, name]);

  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 0));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));

  useImperativeHandle(ref, () => ({
    getPersons: () => {
      return personList}
  }), [personList, userSelected]);

  return (
    <>
      <ScrollSectionTitle
        id="content-associated-persons"
        subTitle={subtitle}
        icon={<BusinessIcon fontSize="large" style={{ color: orange[700] }} />} 
        action={
          <Tooltip title="Add Person">
            <StyledFab onClick={addPerson} color="primary" size="small">
              <AddIcon />
            </StyledFab>
          </Tooltip>
        }
      >
        {title}
      </ScrollSectionTitle>
      <Divider />
      <br />
      <TextField
        label="Search Person"
        variant="outlined"
        fullWidth
        size="small"
        style={{ marginBottom: '1rem', marginTop: '1rem' }}
        value={searchTerm}
        onChange={e => { setSearchTerm(e.target.value); }}
      />
      <DraggableRowsTableStyled
        key={`${searchTerm}-${currentPage}-${filteredList.length}`}
        columns={columns}
        data={paginatedList}
        stickyHeader
        size="small"
        hideHeaders
        showDragIcon={false}
      />
      <PaginationControls>
        <Typography>Page {totalPages > 0 ? currentPage + 1 : 0} of {totalPages} (Total: {filteredList.length} items)</Typography>
        <div>
          <StyledFab onClick={handlePrevPage} disabled={currentPage === 0}>‹</StyledFab>
          <StyledFab onClick={handleNextPage} disabled={currentPage >= totalPages - 1}>›</StyledFab>
        </div>
      </PaginationControls>
    </>
  );
});


const PaddingBottomEntitySearch = styled.div`
  margin-bottom: 15px;
`;

const StyledFab = styled(Fab)`
  width: 35px;
  height: 35px;
  margin: 0 5px;
`;

const PaginationControls = styled(Grid)`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  align-items: center;
`;

const DraggableRowsTableStyled = styled(DraggableRowsTable)`
  margin-bottom: 10px;
`;
